import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Skeleton, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BellOutlined,
  EditOutlined,
  KeyOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  listSubscriptions,
  pauseSubscription,
  resumeSubscription,
  rotateSubscriptionSecret,
  testSubscription,
  type SecretGrant,
  type SubscriptionList,
  type SubscriptionView,
} from '@/api/subscriptions';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { TableSkeleton } from '@/components/TableSkeleton';
import { InfoTip } from '@/help/InfoTip';
import { Term } from '@/help/Term';
import { formatAbsolute } from '@/lib/time';
import { DeliveryLedgerDrawer } from './DeliveryLedgerDrawer';
import { EventCatalogueTable } from './EventCatalogueTable';
import { SecretGrantModal } from './SecretGrantModal';
import { SubscriptionDrawer, type SubscriptionDrawerTarget } from './SubscriptionDrawer';
import { explainWriteRefusal } from './webhookErrors';

const { Text, Paragraph } = Typography;

/** Subscription status → tag + reason; the text carries the meaning, never colour alone. */
function SubscriptionStatus({ sub }: { sub: SubscriptionView }) {
  if (sub.status === 'ACTIVE') {
    return <Tag color="green">ACTIVE</Tag>;
  }
  if (sub.status === 'PAUSED') {
    return (
      <div>
        <Tag color="orange">PAUSED</Tag>
        <div>
          <Text type="secondary" style={{ fontSize: 10.5 }}>
            deliveries queue; resume lets them flow
          </Text>
        </div>
      </div>
    );
  }
  if (sub.status === 'AUTO_DISABLED') {
    return (
      <div>
        <Tag color="red">AUTO_DISABLED</Tag>
        <div>
          <Text type="secondary" style={{ fontSize: 10.5 }}>
            after {sub.consecutiveFailures} consecutive failures — fix the endpoint, then Resume
          </Text>
        </div>
      </div>
    );
  }
  return <Tag>{sub.status}</Tag>;
}

/**
 * The OUTBOUND half of the Integrations page (OPSFL-EVUI-1): the hub's signed webhook
 * subscriptions over the closed event catalogue, the S2 delivery ledger with replay, and the
 * catalogue browser. Surfaces exactly what `/v1/subscriptions/**` serves — writes are a step-up
 * lane (subscriptions.write, hub_admin) and every refusal renders as an explanation. Honest
 * states throughout: no subscriptions is the default (silence), not an error; a 403 names the
 * key and roles; an unavailable API teaches instead of blanking.
 */
export function WebhooksSection() {
  const { message } = App.useApp();
  const [data, setData] = useState<SubscriptionList | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [drawer, setDrawer] = useState<SubscriptionDrawerTarget>(null);
  const [grant, setGrant] = useState<SecretGrant | null>(null);
  const [ledgerFor, setLedgerFor] = useState<SubscriptionView | null>(null);
  /** subscriptionId currently running a row action (ping/pause/resume/rotate). */
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    setUnavailable(false);
    try {
      setData(await listSubscriptions());
    } catch (e) {
      const n = normalizeApiError(e);
      setData(null);
      if (n.httpStatus === 403) setForbidden(true);
      else if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
    // message stable from App context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Run a write action on a row; refusals (step-up, role, state conflict) explained loudly. */
  const rowAction = useCallback(
    async (sub: SubscriptionView, act: () => Promise<void>) => {
      setBusy(sub.subscriptionId);
      try {
        await act();
      } catch (e) {
        message.error(explainWriteRefusal(normalizeApiError(e)));
      } finally {
        setBusy(null);
      }
    },
    [message],
  );

  const ping = useCallback(
    (sub: SubscriptionView) =>
      rowAction(sub, async () => {
        const result = await testSubscription(sub.subscriptionId);
        if (result.state === 'DELIVERED') {
          message.success(
            `Ping DELIVERED — HTTP ${result.statusCode} (delivery ${result.deliveryId}, signed ${result.eventType}).`,
          );
        } else {
          message.warning(
            `Ping ${result.state} — ${
              result.error ?? `HTTP ${result.statusCode}`
            } (delivery ${result.deliveryId}; a test ping never retries).`,
          );
        }
      }),
    [rowAction, message],
  );

  const pauseOrResume = useCallback(
    (sub: SubscriptionView) =>
      rowAction(sub, async () => {
        const view =
          sub.status === 'ACTIVE'
            ? await pauseSubscription(sub.subscriptionId)
            : await resumeSubscription(sub.subscriptionId);
        message.success(`'${view.name}' is now ${view.status}.`);
        await load();
      }),
    [rowAction, message, load],
  );

  const rotate = useCallback(
    (sub: SubscriptionView) =>
      rowAction(sub, async () => {
        const next = await rotateSubscriptionSecret(sub.subscriptionId);
        message.success(`Secret rotated to v${next.subscription.secretVersion} — store it now.`);
        setGrant(next);
        await load();
      }),
    [rowAction, message, load],
  );

  const columns: ColumnsType<SubscriptionView> = [
    {
      title: 'Subscription',
      key: 'name',
      render: (_, r) => (
        <div style={{ minWidth: 200 }}>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <Text type="secondary" style={{ fontSize: 10.5 }} className="ol-mono">
            {r.subscriptionId}
          </Text>
          <div className="ol-mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>
            {r.targetUrl}
          </div>
        </div>
      ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Event types <InfoTip surface="integrations" field="Event types" />
        </span>
      ),
      key: 'eventTypes',
      width: 260,
      render: (_, r) => (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
          {r.eventTypes.map((t) => (
            <Tag key={t} style={{ margin: 0, fontSize: 10.5 }} className="ol-mono">
              {t}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Status <InfoTip surface="integrations" field="Subscription status" />
        </span>
      ),
      key: 'status',
      width: 190,
      render: (_, r) => <SubscriptionStatus sub={r} />,
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Secret <InfoTip surface="integrations" field="Webhook secret" />
        </span>
      ),
      key: 'secret',
      width: 140,
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 11 }} className="ol-mono">
          v{r.secretVersion} · {r.secretFingerprint}…
        </Text>
      ),
    },
    {
      title: 'Created / updated',
      key: 'timestamps',
      width: 170,
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {r.createdAt ? formatAbsolute(r.createdAt) : '—'}
          {r.createdBy ? ` by ${r.createdBy}` : ''}
          <br />
          {r.updatedAt ? `upd ${formatAbsolute(r.updatedAt)}` : ''}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 250,
      align: 'right',
      render: (_, r) => {
        const rowBusy = busy === r.subscriptionId;
        return (
          <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
            <Button
              size="small"
              icon={<SendOutlined />}
              disabled={rowBusy || r.status !== 'ACTIVE'}
              title={r.status !== 'ACTIVE' ? `A ${r.status} subscription cannot ping — resume first.` : 'Send a signed test ping'}
              onClick={() => void ping(r)}
              aria-label={`Send test ping to ${r.name}`}
            >
              Ping
            </Button>
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => setLedgerFor(r)}
              aria-label={`Open delivery ledger for ${r.name}`}
            >
              Deliveries
            </Button>
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={rowBusy}
              onClick={() => setDrawer(r)}
              aria-label={`Edit ${r.name}`}
            >
              Edit
            </Button>
            <Button
              size="small"
              icon={r.status === 'ACTIVE' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              disabled={rowBusy}
              onClick={() => void pauseOrResume(r)}
              aria-label={`${r.status === 'ACTIVE' ? 'Pause' : 'Resume'} ${r.name}`}
            >
              {r.status === 'ACTIVE' ? 'Pause' : 'Resume'}
            </Button>
            <Button
              size="small"
              icon={<KeyOutlined />}
              disabled={rowBusy}
              onClick={() => void rotate(r)}
              aria-label={`Rotate secret for ${r.name}`}
            >
              Rotate secret
            </Button>
          </Space>
        );
      },
    },
  ];

  if (loading && data === null) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (forbidden) {
    return (
      <Alert
        type="info"
        showIcon
        message="Your role can't read event subscriptions"
        description={
          <span>
            Subscriptions are gated by the <Text code>subscriptions.read</Text> permission key,
            held by <Text code>hub_admin</Text>, <Text code>hub_operator</Text> and{' '}
            <Text code>auditor</Text>. The server refused the read; this screen hides nothing
            extra.
          </span>
        }
      />
    );
  }

  if (unavailable) {
    return (
      <EmptyState
        icon={<BellOutlined />}
        title="Outbound event subscriptions"
        description="Signed webhooks over the closed event catalogue land here once the product API's subscription surface is live in this deployment."
        bullets={[
          'Subscriptions: HTTPS endpoint + explicit event types from the closed catalogue',
          'Derived whsec_ signing secrets — shown once at create/rotate, never stored',
          'Signed test ping, pause/resume, secret rotation with a 24h overlap window',
          'Per-subscription delivery ledger: retry ladder, DEAD letters, operator replay',
        ]}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5, flex: 1, minWidth: 280, maxWidth: 800 }}>
          Each <Term id="event-subscription">subscription</Term> is a hub-owned contract: an HTTPS
          endpoint, a <Term id="webhook-secret">signing secret</Term> shown once, and explicit
          event types from the closed <Term id="event-catalogue">catalogue</Term>. Silence is the
          default — nothing is delivered that was not explicitly subscribed. Writes are a step-up
          lane (<Text code>subscriptions.write</Text>, hub_admin).
        </Paragraph>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer('create')}>
            New subscription
          </Button>
        </Space>
      </div>

      <Table<SubscriptionView>
        rowKey="subscriptionId"
        size="small"
        loading={loading && (data?.subscriptions.length ?? 0) > 0}
        columns={columns}
        dataSource={data?.subscriptions ?? []}
        pagination={false}
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading subscriptions" rows={4} />
          ) : (
            <EmptyState
              compact
              icon={<BellOutlined />}
              title="No subscriptions"
              description="The default, not an error. This hub emits nothing outbound until a subscription names an endpoint and the event types it wants; deliveries are then signed and logged in the delivery ledger."
            />
          ),
        }}
      />

      <div style={{ marginTop: 20 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 13 }}>
            Event catalogue
          </Text>
          <Paragraph type="secondary" style={{ margin: '2px 0 0', fontSize: 12 }}>
            The closed list of subscribable event types, as the server declares it. A ratified
            event type stays invisible to subscribers until deliberately admitted here; admin,
            billing and control-plane families are never subscribable.
          </Paragraph>
        </div>
        <EventCatalogueTable types={data?.subscribableEventTypes ?? []} />
      </div>

      <SubscriptionDrawer
        target={drawer}
        subscribableEventTypes={data?.subscribableEventTypes ?? []}
        onClose={() => setDrawer(null)}
        onSaved={() => void load()}
        onSecret={setGrant}
      />
      <SecretGrantModal grant={grant} onClose={() => setGrant(null)} />
      <DeliveryLedgerDrawer subscription={ledgerFor} onClose={() => setLedgerFor(null)} />
    </div>
  );
}
